import { get } from 'lodash'
import {
  dayRangeSize,
  baseDate,
  defaultReminderTime,
} from '../../constants/displayIndex'

/**
 * Gets the first reminder time for an action for a specific user (for sorting purposes).
 * Returns the raw number value in seconds past midnight, or defaults to end of day (midnight).
 *
 * Note: This is different from `getActionReminderTime` in `utils/reminders.js` which returns
 * a formatted string for display. This function is used for sorting/index calculation.
 *
 * @param {Object} action - Action object
 * @param {string} userId - User ID
 * @returns {number} Reminder time in seconds past midnight, or defaultReminderTime
 */
export const getActionReminderTimeForSorting = (action, userId) => {
  const reminderTime = get(
    action,
    ['usersData', userId, 'reminders', 'times', 0],
    defaultReminderTime,
  )
  return reminderTime
}

/**
 * Converts a priority value to its sort order.
 * Returns: Hot=0, None=1, Cold=2 (so Hot < None < Cold for sorting)
 * Priority values: 0 = None, 1 = Cold, 2 = Hot
 *
 * @param {number|null|undefined} priority - Priority value (0 = None, 1 = Cold, 2 = Hot)
 * @returns {number} Priority sort order (0, 1, or 2)
 */
export const getPrioritySortOrder = (priority) => {
  const priorityValue = priority ?? 0
  // Priority 2 (Hot) -> sort order = 0
  // Priority 0 (None) -> sort order = 1
  // Priority 1 (Cold) -> sort order = 2
  if (priorityValue === 2) {
    // Hot
    return 0
  } else if (priorityValue === 0) {
    // None
    return 1
  } else {
    // Cold (priority === 1)
    return 2
  }
}

/**
 * Gets priorityForSorting for an action.
 * Returns: Hot=0, None=1, Cold=2 (so Hot < None < Cold for sorting)
 * Priority values: 0 = None, 1 = Cold, 2 = Hot
 *
 * @param {Object} action - Action object
 * @returns {number} PriorityForSorting value (0, 1, or 2)
 */
export const getActionPriorityForSorting = (action) => {
  return getPrioritySortOrder(action.priority)
}

/**
 * Calculates the day-based offset for a given day.
 * This prevents index collisions when actions from different days roll over.
 * Each day gets its own index range starting at this offset.
 *
 * @param {number|'someday'} dueDay - YYYYMMDD number for the target day, or 'someday'
 * @returns {number} Offset value for the day based on fixed base date (19700101 has offset = 0)
 */
export const calculateDayOffset = (dueDay) => {
  if (dueDay === 'someday') {
    // For "someday" (no scheduled date), use base date + 7 days as reference
    const baseDateStamp = baseDate
    const day7FromBase = baseDateStamp + 7
    return (day7FromBase - baseDateStamp) * dayRangeSize
  }

  // For actual dates (number), calculate offset based on the scheduled date
  const dayStamp = typeof dueDay === 'string' ? parseInt(dueDay, 10) : dueDay
  const dayDifference = dayStamp - baseDate
  return dayDifference * dayRangeSize
}

/**
 * Calculate composite key for sorting/comparison.
 * Matches the logic used in calcDisplayIndex for consistency.
 * Composite key: (day * 1,000,000) + (reminderTime * 10) + priority
 * Uses 1M multiplier for day to avoid JavaScript precision issues with very large numbers
 * This ensures: day > reminderTime > priority ordering
 *
 * @param {number|null|undefined} day - YYYYMMDD day value, or null/undefined for unscheduled
 * @param {number|null|undefined} reminderTime - Reminder time in seconds past midnight, or null
 * @param {number|null|undefined} priority - PriorityForSorting (Hot=0, None=1, Cold=2), or null/undefined
 * @returns {number} Composite key value
 */
export const getCompositeKey = (day, reminderTime, priority) => {
  const dayValue = day != null ? day : -1
  const reminderTimeValue = reminderTime ?? defaultReminderTime
  const priorityValue = priority ?? 1 // Default to None (1) if missing
  return dayValue * 1_000_000 + reminderTimeValue * 10 + priorityValue
}

/**
 * Formats an action for display index calculation.
 * Extracts the relevant fields needed for insertion sorting.
 *
 * @param {Object} action - Action object
 * @param {string} userId - User ID
 * @param {'displayIndex'|'scheduleDisplayIndex'} indexType - Type of index to extract
 * @returns {Object} Formatted action with {day, reminderTime, priority, index}
 */
export const formatActionForCalculation = (action, userId, indexType) => {
  const ud = get(action, ['usersData', userId], {})

  let index
  if (indexType === 'scheduleDisplayIndex') {
    index =
      get(ud, 'scheduleDisplayIndex') ??
      get(action, 'scheduleDisplayIndex', null)
  } else {
    index = get(action, 'displayIndex', null)
  }

  return {
    day: get(action, ['due', 'day']),
    reminderTime: get(ud, ['reminders', 'times', 0], null),
    priority: getActionPriorityForSorting(action),
    index,
  }
}
